import { Button, Chip, Divider, Stack, Tooltip, Typography } from '@mui/material';
import React, { Fragment } from 'react';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { SHARED_APP_LIST_URL } from '../../../constants/routeUrls';
import useModal from '../../../hooks/useModal';
import { useSharedAppLists, useSharedAppListsByIds } from '../../../hooks/useSharedAppLists';
import CollapsiblePaper from '../../shared/CollapsiblePaper';
import SharedAppListSelectorModal from './SharedAppListSelectorModal';

const INCLUDED_APP_AD_SPACE_ID_LISTS = 'targetingProfile.included_app_ad_space_id_lists';
const EXCLUDED_APP_AD_SPACE_ID_LISTS = 'targetingProfile.excluded_app_ad_space_id_lists';
const INCLUDED_APP_BUNDLES_LISTS = 'targetingProfile.included_app_bundles_lists';
const EXCLUDED_APP_BUNDLES_LISTS = 'targetingProfile.excluded_app_bundles_lists';

function TargetingSharedAppLists() {
  const formContext = useFormContext();
  const { getValues, setValue } = formContext;
  const adGroupId = getValues('id');

  const includedAppAdSpaceIdListsRaw = useWatch({ name: INCLUDED_APP_AD_SPACE_ID_LISTS });
  const excludedAppAdSpaceIdListsRaw = useWatch({ name: EXCLUDED_APP_AD_SPACE_ID_LISTS });
  const includedAppBundlesListsRaw = useWatch({ name: INCLUDED_APP_BUNDLES_LISTS });
  const excludedAppBundlesListsRaw = useWatch({ name: EXCLUDED_APP_BUNDLES_LISTS });

  const includedAppAdSpaceIdLists = React.useMemo(() => includedAppAdSpaceIdListsRaw || [], [includedAppAdSpaceIdListsRaw]);
  const excludedAppAdSpaceIdLists = React.useMemo(() => excludedAppAdSpaceIdListsRaw || [], [excludedAppAdSpaceIdListsRaw]);
  const includedAppBundlesLists = React.useMemo(() => includedAppBundlesListsRaw || [], [includedAppBundlesListsRaw]);
  const excludedAppBundlesLists = React.useMemo(() => excludedAppBundlesListsRaw || [], [excludedAppBundlesListsRaw]);

  const { data: sharedAppData } = useSharedAppLists();

  // Get all unique selected IDs
  const allSelectedIds = React.useMemo(() => {
    const ids = new Set([
      ...includedAppAdSpaceIdLists,
      ...excludedAppAdSpaceIdLists,
      ...includedAppBundlesLists,
      ...excludedAppBundlesLists,
    ]);
    return Array.from(ids).filter(Boolean);
  }, [includedAppAdSpaceIdLists, excludedAppAdSpaceIdLists, includedAppBundlesLists, excludedAppBundlesLists]);

  // Find IDs that are not in the default collection
  const missingIds = React.useMemo(() => {
    if (!sharedAppData?.collection || allSelectedIds.length === 0) return [];

    const availableIds = new Set(sharedAppData.collection.map((list) => list.id.toString()));
    return allSelectedIds.filter((id) => !availableIds.has(id));
  }, [sharedAppData?.collection, allSelectedIds]);

  // Fetch missing shared app lists
  const { data: missingListsData } = useSharedAppListsByIds(missingIds);

  // Combine default collection with missing lists
  const combinedCollection = React.useMemo(() => {
    const defaultLists = sharedAppData?.collection || [];
    const missingLists = missingListsData?.collection || [];
    return [...defaultLists, ...missingLists];
  }, [sharedAppData?.collection, missingListsData?.collection]);

  const getSelectedListNames = (value: string[], listType?: string) => {
    if (!combinedCollection.length) return [];

    return combinedCollection
      .filter(
        (list) =>
          value.includes(list.id.toString()) && (!listType || list.list_type === listType || list.listType === listType)
      )
      .map((list) => ({ id: list.id, name: list.name }));
  };

  const handleSave = React.useCallback(
    (fieldName: string, value: string[]) => {
      setValue(fieldName, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue]
  );

  function getNestedValue(obj: FieldValues, path: string): string[] {
    return path.split('.').reduce((prev, curr) => (prev && prev[curr] !== undefined ? prev[curr] : undefined), obj);
  }

  const [showIncludedAppAdSpaceIdLists, closeIncludedAppAdSpaceIdLists] = useModal(
    () => (
      <SharedAppListSelectorModal
        title="Include App Ad Space ID Lists"
        fieldName={INCLUDED_APP_AD_SPACE_ID_LISTS}
        listType="app_ad_space_id"
        onClose={closeIncludedAppAdSpaceIdLists}
        currentValue={getNestedValue(getValues(), INCLUDED_APP_AD_SPACE_ID_LISTS) || []}
        onSave={handleSave}
      />
    ),
    [getValues, handleSave]
  );

  const [showExcludedAppAdSpaceIdLists, closeExcludedAppAdSpaceIdLists] = useModal(
    () => (
      <SharedAppListSelectorModal
        title="Exclude App Ad Space ID Lists"
        fieldName={EXCLUDED_APP_AD_SPACE_ID_LISTS}
        listType="app_ad_space_id"
        onClose={closeExcludedAppAdSpaceIdLists}
        currentValue={getNestedValue(getValues(), EXCLUDED_APP_AD_SPACE_ID_LISTS) || []}
        onSave={handleSave}
      />
    ),
    [getValues, handleSave]
  );

  const [showIncludedAppBundlesLists, closeIncludedAppBundlesLists] = useModal(
    () => (
      <SharedAppListSelectorModal
        title="Include App Bundles Lists"
        fieldName={INCLUDED_APP_BUNDLES_LISTS}
        listType="bundle_id"
        onClose={closeIncludedAppBundlesLists}
        currentValue={getNestedValue(getValues(), INCLUDED_APP_BUNDLES_LISTS) || []}
        onSave={handleSave}
      />
    ),
    [getValues, handleSave]
  );

  const [showExcludedAppBundlesLists, closeExcludedAppBundlesLists] = useModal(
    () => (
      <SharedAppListSelectorModal
        title="Exclude App Bundles Lists"
        fieldName={EXCLUDED_APP_BUNDLES_LISTS}
        listType="bundle_id"
        onClose={closeExcludedAppBundlesLists}
        currentValue={getNestedValue(getValues(), EXCLUDED_APP_BUNDLES_LISTS) || []}
        onSave={handleSave}
      />
    ),
    [getValues, handleSave]
  );

  const includedAppAdSpaceIdListNames = getSelectedListNames(includedAppAdSpaceIdLists, 'app_ad_space_id');
  const excludedAppAdSpaceIdListNames = getSelectedListNames(excludedAppAdSpaceIdLists, 'app_ad_space_id');
  const includedAppBundlesListNames = getSelectedListNames(includedAppBundlesLists, 'bundle_id');
  const excludedAppBundlesListNames = getSelectedListNames(excludedAppBundlesLists, 'bundle_id');

  const renderSelectedLists = (selectedLists) => {
    if (selectedLists.length === 0) return null;

    return (
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {selectedLists.slice(0, 10).map((list, index) => (
          <Fragment key={list.id}>
            {index > 0 && <Divider orientation="vertical" flexItem />}
            <Chip label={list.name} size="small" color="primary" variant="outlined" />
          </Fragment>
        ))}
        {selectedLists.length > 10 && (
          <>
            <Divider orientation="vertical" flexItem />
            <Tooltip
              title={selectedLists
                .slice(10)
                .map((list) => list.name)
                .join(', ')}
            >
              <Chip label={`+${selectedLists.length - 10} more`} size="small" color="primary" variant="outlined" />
            </Tooltip>
          </>
        )}
      </Stack>
    );
  };

  const renderSelectedListsSection = (selectedLists, selectedListNames) => {
    const hasSelectedLists = selectedLists.length > 0;

    return (
      <>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontSize: '14px', whiteSpace: 'nowrap' }}>
          {hasSelectedLists ? 'Selected Lists:' : 'No selected lists'}
        </Typography>

        {hasSelectedLists && (
          <>
            <Divider orientation="vertical" flexItem />
            {renderSelectedLists(selectedListNames)}
          </>
        )}
      </>
    );
  };

  return (
    <CollapsiblePaper
      sessionKey="shared-app-lists"
      title={`Shared App Lists (${
        includedAppAdSpaceIdLists.length +
        excludedAppAdSpaceIdLists.length +
        includedAppBundlesLists.length +
        excludedAppBundlesLists.length
      })`}
    >
      <Stack spacing={3} p={2}>
        {[
          {
            id: 'app-ad-space-id-lists',
            title: 'App Ad Space ID Lists',
            rows: [
              {
                id: 'included-app-ad-space',
                buttonText: 'Manage Included App Ad Space ID Lists',
                onClick: showIncludedAppAdSpaceIdLists,
                selectedLists: includedAppAdSpaceIdLists,
                selectedListNames: includedAppAdSpaceIdListNames,
              },
              {
                id: 'excluded-app-ad-space',
                buttonText: 'Manage Excluded App Ad Space ID Lists',
                onClick: showExcludedAppAdSpaceIdLists,
                selectedLists: excludedAppAdSpaceIdLists,
                selectedListNames: excludedAppAdSpaceIdListNames,
              },
            ],
          },
          {
            id: 'app-bundles-lists',
            title: 'App Bundles Lists',
            rows: [
              {
                id: 'included-app-bundles',
                buttonText: 'Manage Included App Bundles Lists',
                onClick: showIncludedAppBundlesLists,
                selectedLists: includedAppBundlesLists,
                selectedListNames: includedAppBundlesListNames,
              },
              {
                id: 'excluded-app-bundles',
                buttonText: 'Manage Excluded App Bundles Lists',
                onClick: showExcludedAppBundlesLists,
                selectedLists: excludedAppBundlesLists,
                selectedListNames: excludedAppBundlesListNames,
              },
            ],
          },
        ].map((section) => (
          <Fragment key={section.id}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{section.title}</Typography>

              {section.id === 'app-ad-space-id-lists' && (
                <Button variant="text" component={Link} to={SHARED_APP_LIST_URL} target="_blank">
                  View All Shared App Lists
                </Button>
              )}
            </Stack>

            {section.rows.map((row) => (
              <Fragment key={row.id}>
                {row.id.includes('excluded') && <Divider />}

                <Stack direction="row" alignItems="center" spacing={2}>
                  <Button
                    variant="outlined"
                    onClick={row.onClick}
                    disabled={!adGroupId}
                    sx={{ height: '40px', fontSize: '14px', width: '300px', flexShrink: 0 }}
                  >
                    {row.buttonText}
                  </Button>

                  {renderSelectedListsSection(row.selectedLists, row.selectedListNames)}
                </Stack>
              </Fragment>
            ))}
          </Fragment>
        ))}
      </Stack>
    </CollapsiblePaper>
  );
}

export default TargetingSharedAppLists;
